/**
 * WebRTC Streaming Service
 * Handles phone camera streaming for RBT sessions
 * Uses Firebase Firestore for signaling (offer/answer/ICE candidates)
 */

import { db } from '../config/firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
  onSnapshot,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

// STUN/TURN servers for NAT traversal. STUN alone fails on cellular/5G and many
// clinic/home networks; a TURN relay is required for reliable field video.
//
// Primary source: the getIceServers Cloud Function, which returns a TURN relay
// with short-lived credentials (configured server-side via the TURN_CONFIG
// secret). Fallback when that's unavailable — the static env config below:
//   REACT_APP_TURN_URLS             comma-separated turn:/turns: URLs
//   REACT_APP_TURN_USERNAME
//   REACT_APP_TURN_CREDENTIAL
//   REACT_APP_ICE_TRANSPORT_POLICY  'all' (default) | 'relay' (force TURN, e.g. to verify relay works)
function iceTransportPolicy(): RTCIceTransportPolicy {
  return process.env.REACT_APP_ICE_TRANSPORT_POLICY === 'relay' ? 'relay' : 'all';
}

let iceCache: { config: RTCConfiguration; expiresAt: number } | null = null;

async function getIceConfig(): Promise<RTCConfiguration> {
  // Reuse credentials until an hour before they expire.
  if (iceCache && iceCache.expiresAt - Date.now() > 60 * 60 * 1000) return iceCache.config;
  try {
    const call = httpsCallable<void, { iceServers: RTCIceServer[]; expiresAt: number; relay: boolean }>(
      getFunctions(),
      'getIceServers'
    );
    const { data } = await call();
    if (!data.relay) console.warn('[WebRTC] No TURN relay configured — video may fail off Wi-Fi.');
    const config: RTCConfiguration = { iceServers: data.iceServers, iceTransportPolicy: iceTransportPolicy() };
    iceCache = { config, expiresAt: data.expiresAt };
    return config;
  } catch (err) {
    console.error('[WebRTC] getIceServers failed — using built-in STUN/TURN config', err);
    return ICE_SERVERS;
  }
}

function buildIceServers(): RTCConfiguration {
  const servers: RTCIceServer[] = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
  ];

  const turnUrls = (process.env.REACT_APP_TURN_URLS || '')
    .split(',')
    .map((u) => u.trim())
    .filter(Boolean);
  if (turnUrls.length) {
    servers.push({
      urls: turnUrls,
      username: process.env.REACT_APP_TURN_USERNAME || undefined,
      credential: process.env.REACT_APP_TURN_CREDENTIAL || undefined,
    });
  }

  return { iceServers: servers, iceTransportPolicy: iceTransportPolicy() };
}

const ICE_SERVERS: RTCConfiguration = buildIceServers();

export interface StreamSession {
  id: string;
  sessionNoteId: string;
  rbtId: string;
  rbtName: string;
  supervisorId: string;
  companyId: string;
  status: 'waiting' | 'connecting' | 'live' | 'ended';
  offer?: RTCSessionDescriptionInit;
  answer?: RTCSessionDescriptionInit;
  createdAt: Timestamp;
  startedAt?: Timestamp;
  endedAt?: Timestamp;
}

class WebRTCService {
  private peerConnection: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;
  private remoteStream: MediaStream | null = null;
  private streamSessionId: string | null = null;
  private unsubscribeAnswer: (() => void) | null = null;
  // Remote ICE candidates that arrive before the remote description is set.
  // addIceCandidate would throw and drop them — on cellular those are often the
  // only candidates that work (the TURN relay ones), so queue and flush.
  private pendingCandidates: RTCIceCandidateInit[] = [];

  private async addRemoteCandidate(init: RTCIceCandidateInit): Promise<void> {
    const pc = this.peerConnection;
    if (!pc) return;
    if (!pc.remoteDescription) {
      this.pendingCandidates.push(init);
      return;
    }
    try {
      await pc.addIceCandidate(new RTCIceCandidate(init));
    } catch (e) {
      console.error('[WebRTC] addIceCandidate failed', e);
    }
  }

  private async flushPendingCandidates(): Promise<void> {
    const queued = this.pendingCandidates;
    this.pendingCandidates = [];
    for (const c of queued) await this.addRemoteCandidate(c);
  }
  private unsubscribeIceCandidates: (() => void) | null = null;

  /**
   * Initialize phone camera stream (for RBT)
   */
  async startLocalStream(
    videoElement: HTMLVideoElement,
    facingMode: 'user' | 'environment' = 'environment'
  ): Promise<MediaStream> {
    try {
      // Request camera with mobile-optimized settings
      this.localStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          frameRate: { ideal: 30, max: 30 },
        },
        audio: true,
      });

      // Display in video element
      videoElement.srcObject = this.localStream;
      videoElement.muted = true; // Prevent echo
      await videoElement.play();

      return this.localStream;
    } catch (error: any) {
      console.error('Camera access error:', error);
      if (error.name === 'NotAllowedError') {
        throw new Error('Camera permission denied. Please allow camera access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        throw new Error('No camera found. Please ensure your device has a camera.');
      } else if (error.name === 'NotReadableError') {
        throw new Error('Camera is in use by another application.');
      }
      throw new Error('Failed to access camera: ' + error.message);
    }
  }

  /**
   * Enable/disable the outgoing local media (video + audio). Used by Privacy
   * Mode to pause what the supervisor sees/hears without tearing down the
   * WebRTC connection. Disabled tracks transmit black frames / silence.
   */
  setMediaEnabled(enabled: boolean): void {
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => {
        track.enabled = enabled;
      });
    }
  }

  /**
   * Switch between front and back camera
   */
  async switchCamera(
    videoElement: HTMLVideoElement,
    currentFacingMode: 'user' | 'environment'
  ): Promise<'user' | 'environment'> {
    const newFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';

    // Stop current tracks
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
    }

    // Start new stream with switched camera
    await this.startLocalStream(videoElement, newFacingMode);

    // If peer connection exists, replace the video track
    if (this.peerConnection && this.localStream) {
      const videoTrack = this.localStream.getVideoTracks()[0];
      const sender = this.peerConnection.getSenders().find(s => s.track?.kind === 'video');
      if (sender && videoTrack) {
        await sender.replaceTrack(videoTrack);
      }
    }

    return newFacingMode;
  }

  /**
   * Create a streaming session and start broadcasting (RBT side)
   */
  async createStreamSession(
    sessionNoteId: string,
    rbtId: string,
    rbtName: string,
    supervisorId: string,
    companyId: string
  ): Promise<string> {
    if (!this.localStream) {
      throw new Error('Local stream not started. Call startLocalStream first.');
    }

    // Reap any of this RBT's still-open streams before starting a new one, so a
    // session that ended without a clean stop (tab closed, navigation) can't
    // leave a stale "waiting" doc inflating the supervisor's Live Now count.
    try {
      await this.endActiveStreamsForRbt(rbtId);
    } catch (e) {
      console.error('Failed to reap prior streams (non-blocking):', e);
    }

    // Create peer connection (TURN relay credentials from getIceServers)
    this.pendingCandidates = [];
    this.peerConnection = new RTCPeerConnection(await getIceConfig());

    // Add local tracks to peer connection
    this.localStream.getTracks().forEach(track => {
      this.peerConnection!.addTrack(track, this.localStream!);
    });

    // Create stream session document in Firestore
    const streamSessionRef = doc(collection(db, 'streamSessions'));
    this.streamSessionId = streamSessionRef.id;

    const streamSession: Omit<StreamSession, 'id'> = {
      sessionNoteId,
      rbtId,
      rbtName,
      supervisorId,
      companyId,
      status: 'waiting',
      createdAt: Timestamp.now(),
    };

    await setDoc(streamSessionRef, streamSession);

    // Handle ICE candidates
    this.peerConnection.onicecandidate = async (event) => {
      if (event.candidate) {
        const candidateRef = doc(
          collection(db, 'streamSessions', this.streamSessionId!, 'offerCandidates')
        );
        await setDoc(candidateRef, event.candidate.toJSON());
      }
    };

    // Create offer
    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);

    // Save offer to Firestore
    await updateDoc(streamSessionRef, {
      offer: {
        type: offer.type,
        sdp: offer.sdp,
      },
      status: 'waiting',
    });

    // Listen for answer from supervisor
    this.unsubscribeAnswer = onSnapshot(streamSessionRef, async (snapshot) => {
      const data = snapshot.data() as StreamSession;
      if (data?.answer && this.peerConnection && !this.peerConnection.currentRemoteDescription) {
        const answerDescription = new RTCSessionDescription(data.answer);
        await this.peerConnection.setRemoteDescription(answerDescription);
        await this.flushPendingCandidates();
      }
    });

    // Listen for ICE candidates from supervisor (queued until the answer lands)
    this.unsubscribeIceCandidates = onSnapshot(
      collection(db, 'streamSessions', this.streamSessionId, 'answerCandidates'),
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            void this.addRemoteCandidate(change.doc.data() as RTCIceCandidateInit);
          }
        });
      }
    );

    return this.streamSessionId;
  }

  /**
   * Join a streaming session (Supervisor side)
   */
  async joinStreamSession(
    streamSessionId: string,
    remoteVideoElement: HTMLVideoElement
  ): Promise<void> {
    this.streamSessionId = streamSessionId;
    this.remoteStream = new MediaStream();
    remoteVideoElement.srcObject = this.remoteStream;

    // Create peer connection (TURN relay credentials from getIceServers)
    this.pendingCandidates = [];
    this.peerConnection = new RTCPeerConnection(await getIceConfig());

    // Handle incoming tracks
    this.peerConnection.ontrack = (event) => {
      event.streams[0].getTracks().forEach(track => {
        this.remoteStream!.addTrack(track);
      });
    };

    // Get the stream session document
    const streamSessionRef = doc(db, 'streamSessions', streamSessionId);
    const streamSessionSnap = await getDoc(streamSessionRef);

    if (!streamSessionSnap.exists()) {
      throw new Error('Stream session not found');
    }

    const streamSession = streamSessionSnap.data() as StreamSession;

    if (!streamSession.offer) {
      throw new Error('No offer found in stream session');
    }

    // Set remote description (the offer)
    await this.peerConnection.setRemoteDescription(
      new RTCSessionDescription(streamSession.offer)
    );

    // Handle ICE candidates
    this.peerConnection.onicecandidate = async (event) => {
      if (event.candidate) {
        const candidateRef = doc(
          collection(db, 'streamSessions', streamSessionId, 'answerCandidates')
        );
        await setDoc(candidateRef, event.candidate.toJSON());
      }
    };

    // Listen for ICE candidates from RBT
    this.unsubscribeIceCandidates = onSnapshot(
      collection(db, 'streamSessions', streamSessionId, 'offerCandidates'),
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            void this.addRemoteCandidate(change.doc.data() as RTCIceCandidateInit);
          }
        });
      }
    );

    // Create answer
    const answer = await this.peerConnection.createAnswer();
    await this.peerConnection.setLocalDescription(answer);

    // Save answer to Firestore
    await updateDoc(streamSessionRef, {
      answer: {
        type: answer.type,
        sdp: answer.sdp,
      },
      status: 'live',
      startedAt: serverTimestamp(),
    });

    await remoteVideoElement.play();
  }

  /**
   * Get connection state
   */
  getConnectionState(): RTCPeerConnectionState | null {
    return this.peerConnection?.connectionState || null;
  }

  /**
   * Check if stream is active
   */
  isStreamActive(): boolean {
    return this.peerConnection?.connectionState === 'connected';
  }

  /**
   * Get stream session info
   */
  async getStreamSession(streamSessionId: string): Promise<StreamSession | null> {
    const streamSessionRef = doc(db, 'streamSessions', streamSessionId);
    const snapshot = await getDoc(streamSessionRef);

    if (!snapshot.exists()) {
      return null;
    }

    return { id: snapshot.id, ...snapshot.data() } as StreamSession;
  }

  /**
   * Get active stream for a session note
   */
  async getActiveStreamForSession(sessionNoteId: string): Promise<StreamSession | null> {
    const { query, where, getDocs } = await import('firebase/firestore');

    const q = query(
      collection(db, 'streamSessions'),
      where('sessionNoteId', '==', sessionNoteId),
      where('status', 'in', ['waiting', 'connecting', 'live'])
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() } as StreamSession;
  }

  /**
   * End all of an RBT's still-open streams (waiting/connecting/live). Used to
   * dedupe before a new stream starts and to reap leaked docs from sessions that
   * ended without a clean stop.
   */
  async endActiveStreamsForRbt(rbtId: string): Promise<void> {
    const q = query(
      collection(db, 'streamSessions'),
      where('rbtId', '==', rbtId),
      where('status', 'in', ['waiting', 'connecting', 'live'])
    );
    const snap = await getDocs(q);
    if (snap.empty) return;
    const batch = writeBatch(db);
    snap.forEach((d) =>
      batch.update(d.ref, { status: 'ended', endedAt: serverTimestamp() })
    );
    await batch.commit();
  }

  /**
   * End a specific stream by id (supervisor "dismiss"/end from Live Streams).
   */
  async endStreamById(streamId: string): Promise<void> {
    await updateDoc(doc(db, 'streamSessions', streamId), {
      status: 'ended',
      endedAt: serverTimestamp(),
    });
  }

  /**
   * Supervisor leaves a stream WITHOUT ending it. Tears down only the local
   * viewer connection/listeners and leaves the streamSessions doc alive, so the
   * RBT keeps broadcasting and the supervisor can rejoin. (endStream, by
   * contrast, marks the doc 'ended' — that's for actually stopping the stream.)
   */
  leaveStream(): void {
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }
    if (this.unsubscribeAnswer) {
      this.unsubscribeAnswer();
      this.unsubscribeAnswer = null;
    }
    if (this.unsubscribeIceCandidates) {
      this.unsubscribeIceCandidates();
      this.unsubscribeIceCandidates = null;
    }
    this.remoteStream = null;
    // Do NOT touch this.streamSessionId's Firestore doc — the stream lives on.
    this.streamSessionId = null;
  }

  /**
   * End streaming session
   */
  async endStream(): Promise<void> {
    // Stop local tracks
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }

    // Close peer connection
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    // Unsubscribe from listeners
    if (this.unsubscribeAnswer) {
      this.unsubscribeAnswer();
      this.unsubscribeAnswer = null;
    }
    if (this.unsubscribeIceCandidates) {
      this.unsubscribeIceCandidates();
      this.unsubscribeIceCandidates = null;
    }

    // Update Firestore
    if (this.streamSessionId) {
      const streamSessionRef = doc(db, 'streamSessions', this.streamSessionId);
      await updateDoc(streamSessionRef, {
        status: 'ended',
        endedAt: serverTimestamp(),
      });
      this.streamSessionId = null;
    }

    this.remoteStream = null;
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }
    if (this.unsubscribeAnswer) {
      this.unsubscribeAnswer();
    }
    if (this.unsubscribeIceCandidates) {
      this.unsubscribeIceCandidates();
    }
  }
}

export const webRTCService = new WebRTCService();
